import { Link } from "react-router-dom";
import { ArrowUpRight, Clock } from "lucide-react";
import { useLang } from "@/i18n";
import { MaskedLines, Reveal, FadeIn, ParallaxImage, Kicker } from "@/components/motion";
import { ARTICLES } from "@/articles";

export default function Insights() {
  const { t, lang } = useLang();
  const s = t.insights;

  return (
    <>
      <section className="relative overflow-hidden pt-32 pb-16 sm:pt-40 sm:pb-20 grain" data-testid="insights-hero">
        <div className="pointer-events-none absolute -left-40 top-0 h-[420px] w-[420px] rounded-full bg-sage-100/70 blur-3xl" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
          <FadeIn delay={0.1}>
            <Kicker>{s.kicker}</Kicker>
          </FadeIn>
          <MaskedLines
            className="mt-9 max-w-3xl font-serif text-4xl leading-[1.1] tracking-tight text-navy-900 sm:text-5xl lg:text-6xl"
            delay={0.25}
            lines={[s.title]}
          />
          <Reveal delay={0.6}>
            <p className="mt-7 max-w-2xl text-base leading-relaxed text-navy-900/70 sm:text-lg" data-testid="insights-intro">
              {s.intro}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="pb-20 sm:pb-28" data-testid="insights-list">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="grid gap-10 lg:grid-cols-3">
            {ARTICLES.map((article, i) => {
              const c = article[lang];
              return (
                <Reveal key={article.slug} delay={i * 0.1} className="group flex flex-col border border-navy-900/10 bg-white transition-shadow duration-500 hover:shadow-xl">
                  <Link to={`/insights/${article.slug}`} data-testid={`insight-card-${article.slug}`}>
                    <ParallaxImage
                      src={article.image}
                      alt={c.title}
                      testId={`insight-image-${i}`}
                      className="aspect-[16/10] w-full"
                      speed={0.08}
                    />
                  </Link>
                  <div className="flex flex-1 flex-col p-7">
                    <p className="text-[11px] uppercase tracking-[0.22em] text-sage-700" data-testid={`insight-category-${i}`}>{c.category}</p>
                    <h2 className="mt-3 font-serif text-xl leading-snug tracking-tight text-navy-900" data-testid={`insight-title-${i}`}>
                      <Link to={`/insights/${article.slug}`} className="transition-colors hover:text-sage-700">
                        {c.title}
                      </Link>
                    </h2>
                    <p className="mt-3 flex-1 text-sm leading-relaxed text-navy-900/60">{c.excerpt}</p>
                    <div className="mt-5 flex items-center justify-between">
                      <p className="flex items-center gap-2 font-mono text-[11px] text-navy-900/40">
                        <Clock size={12} /> {article.readTime} {s.readTime}
                      </p>
                      <Link
                        to={`/insights/${article.slug}`}
                        className="inline-flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-[0.16em] text-gold-deep transition-colors hover:text-navy-900"
                        data-testid={`insight-read-${i}`}
                      >
                        {s.readArticle} <ArrowUpRight size={13} />
                      </Link>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
}
